import { SecuritySymbolHelper } from '../../domain/symbol.domain';
import type { SecuritySymbol } from '../../domain/symbol.domain';
import type { AutoSuggestSymbol } from '../../domain/symbol-lookup.domain';

export interface SymbolLookupSelectionResolution {
  readonly kind: 'noop' | 'select' | 'setModel';
  readonly selected?: SecuritySymbol | AutoSuggestSymbol;
  readonly nextModel?: SecuritySymbol | AutoSuggestSymbol;
}

export interface SymbolLookupSelectionResolverOptions {
  readonly inputValue: string | null | undefined;
  readonly symbols: Array<AutoSuggestSymbol>;
  readonly isValid: boolean;
  readonly lenient: boolean;
}

/**
 * Resolves what symbol-lookup should do when the user confirms the current input.
 *
 * This intentionally preserves the historical symbol-lookup semantics:
 * - valid input prefers the first autosuggest result when present
 * - otherwise valid input falls back to parsing the text directly
 * - invalid lenient input keeps a free-form model
 * - invalid strict input clears the model
 */
export function resolveSymbolLookupSelection(
  options: SymbolLookupSelectionResolverOptions
): SymbolLookupSelectionResolution {
  const {
    inputValue,
    symbols,
    isValid,
    lenient
  }: SymbolLookupSelectionResolverOptions = options;

  if (inputValue === undefined || inputValue === null || inputValue === '') {
    return { kind: 'noop' };
  }

  if (isValid) {
    if (symbols.length > 0) {
      return { kind: 'select', selected: symbols[0] };
    }

    return { kind: 'select', selected: SecuritySymbolHelper.parse<SecuritySymbol>(inputValue) };
  }

  if (lenient) {
    return { kind: 'setModel', nextModel: { symbol: inputValue } };
  }

  return { kind: 'setModel', nextModel: undefined };
}
